import moment from 'moment';
import {LeanDocument} from 'mongoose';
import {CourseStudentItemDocument} from '@/documentTypes/course-manager/Course.document';
import {TimetableCancellationReason} from '@/enums/scheduler/TimetableCancellationReason.enum';

/**
 * Shared rules for single timetable occurrences (lessons), used by the Today view, the calendar
 * dialog and the student attendance table so that a lesson shows the same state everywhere.
 */

type StudentItem = LeanDocument<CourseStudentItemDocument>;

const EVENT_DATE_FORMATS = ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD'];

/** Server timetable strings ("YYYY-MM-DD HH:mm") are parsed explicitly, new Date() fails on them in some browsers */
export function parseEventDate(value: string): moment.Moment {
    const parsed = moment(value, EVENT_DATE_FORMATS, true);
    return parsed.isValid() ? parsed : moment(value);
}

/** A course log belongs to an occurrence when its `payload.instance` lies on the same day as the occurrence start */
export function isSameOccurrenceDay(instance: Date | string | undefined, eventStart: string | moment.Moment): boolean {
    if (!instance) {
        return false;
    }
    const start = typeof eventStart === 'string' ? parseEventDate(eventStart) : eventStart;
    return moment(instance).isSame(start, 'day');
}

/**
 * Text if the participation of the student item ended (and was not resumed) before `day`, otherwise undefined.
 * Participation lasts until the end of the month of endOfParticipation (same display as in the calendar).
 */
export function participationEndedText(item: StudentItem, day: moment.Moment): string | undefined {
    if (!item.endOfParticipation) {
        return undefined;
    }
    const endsAt = moment(item.endOfParticipation).endOf('month');
    if (!day.isAfter(endsAt)) {
        return undefined;
    }
    if (item.resumptionOfParticipation && !moment(item.resumptionOfParticipation).isAfter(day, 'day')) {
        return undefined;
    }
    return `Teilnahme bis ${endsAt.format('DD.MM.YYYY')}`;
}

/** Student items that still take part in the occurrence starting at `eventStart` */
export function participatingStudentItems<T extends StudentItem>(items: T[] | undefined, eventStart: string): T[] {
    const day = parseEventDate(eventStart);
    return (items || []).filter((item) => !participationEndedText(item, day));
}

/** Text of the cancellation reason of a lesson (same texts as on the Today view) */
export function cancellationReasonText(reason?: TimetableCancellationReason): string {
    return reason ? TimetableCancellationReason.Record[reason]?.text || 'Ausfall' : 'Ausfall';
}
